import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import ComicCard from '@/components/base/ComicCard';
import { comics, categories } from '@/mocks/comics';
import type { Comic } from '@/mocks/comics';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const [activeSlug, setActiveSlug] = useState(slug || 'all');
  const [filteredComics, setFilteredComics] = useState<Comic[]>([]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeSlug]);

  useEffect(() => {
    setActiveSlug(slug || 'all');
  }, [slug]);

  useEffect(() => {
    if (activeSlug === 'all') {
      setFilteredComics(comics);
    } else {
      setFilteredComics(comics.filter((c) => c.category === activeSlug));
    }
  }, [activeSlug]);

  const currentCat = categories.find((c) => c.slug === activeSlug);
  const catName = currentCat?.name || '全部漫画';

  const pageTitle = activeSlug === 'all'
    ? '漫画大全 - 全集漫画免费完整版在线阅读 | 樱花漫画网'
    : `${catName}专区 - 全集${catName}免费完整版下拉式阅读 | 樱花漫画网`;

  const pageDesc = activeSlug === 'all'
    ? '樱花漫画网漫画大全频道，全集漫画免费完整版下拉式在线阅读，涵盖韩漫、日漫、国漫等各种类型好看的漫画全本推荐。'
    : `樱花漫画网${catName}专区，提供全集${catName}免费完整版下拉式在线阅读，精选好看的${catName}全本漫画推荐，一次看个够。`;

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://cloudculture.cn' },
      { '@type': 'ListItem', position: 2, name: catName, item: `https://cloudculture.cn/category/${activeSlug}` },
    ],
  };

  return (
    <>
      <SEO
        title={pageTitle}
        description={pageDesc}
        keywords={`全集漫画,漫画全集,${catName},樱花漫画网,下拉式阅读,免费漫画,全本漫画`}
        canonical={`https://cloudculture.cn/category/${activeSlug}`}
        ogTitle={pageTitle}
        ogDescription={pageDesc}
        ogType="website"
        ogUrl={`https://cloudculture.cn/category/${activeSlug}`}
        twitterCard="summary_large_image"
        jsonLd={[breadcrumbSchema]}
      />

      <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-8">
        <nav className="flex items-center gap-2 text-sm text-foreground-400 mb-6" aria-label="面包屑导航">
          <a href="/" className="hover:text-primary-600 transition-colors whitespace-nowrap">首页</a>
          <span>/</span>
          <span className="text-foreground-600">{catName}</span>
        </nav>

        <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">
          {activeSlug === 'all' ? '漫画大全' : `${catName}专区`}
        </h1>

        <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2">
          {categories.map((cat) => (
            <Link
              key={cat.slug}
              to={`/category/${cat.slug}`}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                activeSlug === cat.slug
                  ? 'bg-primary-500 text-background-50'
                  : 'border border-background-200/70 text-foreground-500 hover:text-primary-600 hover:border-primary-300'
              }`}
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className={`${cat.icon} text-xs`}></i>
              </div>
              {cat.name}
            </Link>
          ))}
        </div>

        {filteredComics.length > 0 ? (
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-4 md:gap-5" data-product-shop>
            {filteredComics.map((comic) => (
              <ComicCard key={comic.id} comic={comic} size="medium" />
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-emotion-sad-line text-2xl text-foreground-400"></i>
            </div>
            <p className="text-foreground-500">暂无漫画内容</p>
          </div>
        )}
      </div>
    </>
  );
}